import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Eye, Plus, X } from "lucide-react";
import toast from "react-hot-toast";

import {
    createAssignmentService,
    getAssignmentsService,
    getDeliveryEmployeesService,
    getParentsService,
} from "@/services/deliveryAgent";

import Dropdown from "../ui/Dropdown";
import GenericTable from "../ui/GenericTable";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import { useRoleContext } from "@/hooks/useRoleContext";
import { getMasterDropdownService } from "@/services/masters";
import DatePicker from "../ui/DatePicker";
import { getUnAssignedChildren } from "@/services/parent";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const deliverySchema = z.object({
    branch: z.string().min(1, "Branch is required"),
    parent: z.string().min(1, "Parent is required"),
    employee: z.string().min(1, "Delivery Person is required"),
    start_date: z.string().min(1, "Start Date is required"),
    end_date: z.string().optional(),
    child_ids: z.array(z.string()).min(1, "Select at least one child"),
});

type FormValues = z.infer<typeof deliverySchema>;

const safeArray = (data: unknown): any[] => {
    if (Array.isArray(data)) return data;
    if (data && typeof data === "object" && "results" in data) {
        return (data as any).results;
    }
    return [];
};

export default function DeliveryAssignment() {
    const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors },
} = useForm<FormValues>({
        resolver: zodResolver(deliverySchema),
        defaultValues: {
            branch: "",
            parent: "",
            employee: "",
            start_date: "",
            end_date: "",
            child_ids: [],
        },
    });

    const [parents, setParents] = useState<any[]>([]);
    const [employees, setEmployees] = useState<any[]>([]);
    const [assignments, setAssignments] = useState<any[]>([]);
    const [branches, setBranches] = useState<any[]>([]);

    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [childrenOptions, setChildrenOptions] = useState<any[]>([]);
    const [openUnassignedModal, setOpenUnassignedModal] = useState(false);
    const [unassignedLoading, setUnassignedLoading] = useState(false);
    const [unassignedChildren, setUnassignedChildren] = useState<any[]>([]);
    const [expandedParent, setExpandedParent] = useState<number | null>(null);
    const { isBranchIncharge, isZoneManager, branchCode, zoneCode } = useRoleContext();
    const { canView, canCreate } = useScreenPermission("delivery assign");
    const showBranchDropdown = !isBranchIncharge;
    const selectedParent = watch("parent");

    useEffect(() => {
        if (openModal || openUnassignedModal) {
            document.body.style.overflow = "hidden";
            document.body.style.touchAction = "none";
        } else {
            document.body.style.overflow = "auto";
            document.body.style.touchAction = "auto";
        }

        return () => {
            document.body.style.overflow = "auto";
            document.body.style.touchAction = "auto";
        };
    }, [openModal, openUnassignedModal]);

    useEffect(() => {
        if (!selectedParent) {
            setChildrenOptions([]);
            return;
        }

        const parentData = parents.find(
            (p) => p.Username === selectedParent
        );

        if (!parentData?.Details) {
            setChildrenOptions([]);
            return;
        }

        const options = parentData.Details.flatMap((item: any) =>
            item.Child.map((child: any) => ({
                id: child[1], // child id
                value: child[1],
                label: `${child[0]} - ${item.Pickup_Address}`,
            }))
        );

        setChildrenOptions(options);
    }, [selectedParent, parents]);

    const fetchUnassignedChildren = async () => {
        try {
            setUnassignedLoading(true);

            const res = await getUnAssignedChildren(
                isBranchIncharge ? branchCode : "",
                isZoneManager ? zoneCode : ""
            );

            if (res?.status === 200) {
                setUnassignedChildren(safeArray(res.data));
                setOpenUnassignedModal(true);
            }
        } catch {
            toast.error("Failed to fetch unassigned children");
        } finally {
            setUnassignedLoading(false);
        }
    };

    const fetchBranches = async () => {
        try {
            const res = await getMasterDropdownService();

            if (res?.status === 200) {
                const raw = res.data as any;
                const data = raw?.Branches || raw?.results || raw || [];

                setBranches(
                    safeArray(data).map((b: any) => ({
                        id: b.unique_code,
                        label: `${b.Branch_Name}`,
                        value: b.unique_code,
                    }))
                );
            }
        } catch {
            toast.error("Failed to load branches");
        }
    };

    const fetchParents = async () => {
        try {
            let params = "";
            if (isBranchIncharge) params = `Branch_Code=${branchCode}`;
            if (isZoneManager) params = `Zone_Code=${zoneCode}`;

            const res = await getParentsService(params);
            if (res?.status === 200) setParents(safeArray(res.data));
        } catch {
            toast.error("Failed to fetch parents");
        }
    };

    const fetchAssignments = async () => {
        if (!canView) return;

        setLoading(true);
        try {
            let params = "";
            if (isBranchIncharge) params = `Branch_Code=${branchCode}`;
            if (isZoneManager) params = `Zone_Code=${zoneCode}`;

            const res = await getAssignmentsService(params);
            if (res?.status === 200) setAssignments(safeArray(res.data));
        } catch {
            toast.error("Failed to fetch assignments");
        } finally {
            setLoading(false);
        }
    };

    const handleBranchChange = async (branch: string | number) => {
        const branchStr = String(branch);

        setParents([]);
        setEmployees([]);
        setChildrenOptions([]);

        reset({
            branch: branchStr,
            parent: "",
            employee: "",
            start_date: "",
            end_date: "",
            child_ids: [],
        });

        try {
            let query = `Status=delivery&Branch_Code=${branchStr}`;

            if (isZoneManager) {
                query += `&Zone_Code=${zoneCode}`;
            }

            const empRes = await getDeliveryEmployeesService(query);
            if (empRes?.status === 200) {
                setEmployees(safeArray(empRes.data));
            }

            const parentRes = await getParentsService(`Branch_Code=${branchStr}`);
            if (parentRes?.status === 200) {
                setParents(safeArray(parentRes.data));
            }
        } catch {
            toast.error("Failed to fetch data");
        }
    };

    const fetchEmployeesByZone = async () => {
        try {
            const res = await getDeliveryEmployeesService(
                `Zone_Code=${zoneCode}&Status=delivery`
            );

            if (res?.status === 200) {
                setEmployees(safeArray(res.data));
            }
        } catch {
            toast.error("Failed to fetch zone delivery boys");
        }
    };

const onSubmit = async (values: FormValues) => {
    if (!canCreate) {
        toast.error("No permission");
        return;
    }

    setSubmitLoading(true);

    try {
        const payload = {
    Start_Date: values.start_date,

    ...(values.end_date && {
        End_Date: values.end_date,
    }),

    Parent_Username: values.parent,
    Employee_Username: values.employee,
    Child_Id: values.child_ids,
    Branch_Code: showBranchDropdown
        ? values.branch
        : branchCode,
};

        const res = await createAssignmentService(payload);

        if (res?.status === 200 || res?.status === 201) {

            toast.success("Assigned successfully ✅");

            reset({
                parent: "",
                employee: "",
                branch: isBranchIncharge ? branchCode : "",
                start_date: "",
                end_date: "",
                child_ids: [],
            });

            setChildrenOptions([]);
            setEmployees([]);
            setOpenModal(false);

            fetchAssignments();

            return;
        }

        // API VALIDATION ERRORS
        const apiErrors = res?.errors;

        if (apiErrors && typeof apiErrors === "object") {

            const fieldMap: Record<string, keyof FormValues> = {
                Branch_Code: "branch",
                Parent_Username: "parent",
                Employee_Username: "employee",
                Start_Date: "start_date",
                End_Date: "end_date",
                Child_Id: "child_ids",
            };

            Object.entries(apiErrors).forEach(
                ([field, messages]) => {

                    const formField = fieldMap[field];

                    const message = Array.isArray(messages)
                        ? messages[0]
                        : String(messages);

                    if (formField) {
                        setError(formField, {
                            type: "server",
                            message,
                        });
                    } else {
                        toast.error(message);
                    }
                }
            );

        } else {
            toast.error("Submission failed");
        }

    } catch (error: any) {

        // console.log(error?.response?.data);

        const apiErrors = error?.response?.data;

        if (apiErrors && typeof apiErrors === "object") {

            const fieldMap: Record<string, keyof FormValues> = {
                Branch_Code: "branch",
                Parent_Username: "parent",
                Employee_Username: "employee",
                Start_Date: "start_date",
                End_Date: "end_date",
                Child_Id: "child_ids",
            };

            Object.entries(apiErrors).forEach(
                ([field, messages]) => {

                    const formField = fieldMap[field];

                    const message = Array.isArray(messages)
                        ? messages[0]
                        : String(messages);

                    if (formField) {
                        setError(formField, {
                            type: "server",
                            message,
                        });
                    } else {
                        toast.error(message);
                    }
                }
            );

        } else {
            toast.error("Submission failed");
        }

    } finally {
        setSubmitLoading(false);
    }
};

    useEffect(() => {
        if (!canView) return;

        fetchBranches();
        fetchAssignments();

        if (canCreate && (isBranchIncharge || isZoneManager)) {
            fetchParents();
        }
    }, [canView, canCreate, isBranchIncharge, isZoneManager]);

    useEffect(() => {
        if (isBranchIncharge) {
            reset({
                parent: "",
                employee: "",
                branch: branchCode,
                start_date: "",
                end_date: "",
                child_ids: [],
            });
        }
    }, [branchCode]);

    useEffect(() => {
        if (isBranchIncharge) {
            handleBranchChange(branchCode);
        }

        if (isZoneManager) {
            fetchEmployeesByZone();
        }
    }, [branchCode, zoneCode, isBranchIncharge]);

    const parentOptions = parents.map((p) => ({
        id: p.user,
        label: `${p.First_Name} ${p.Last_Name} (${p.deliveries})`,
        value: p.Username,
    }));

    const employeeOptions = employees.map((e) => ({
        id: e.user,
        label: e.user__username,
        value: e.user__username,
    }));

    const columns = [
        { key: "Parent_Username", label: "Parent" },
        { key: "Employee_Username", label: "Delivery Person" },
        { key: "Start_Date", label: "Start Date" },
        { key: "End_Date", label: "End Date" },
        { key: "Created_By", label: "Assigned By" },
    ];

    const handleCloseModal = () => {
        setOpenModal(false);

        if (isBranchIncharge) {
            reset({
                branch: branchCode,
                parent: "",
                employee: "",
                start_date: "",
                end_date: "",
                child_ids: []
            });
        } else {
            reset({
                branch: "",
                parent: "",
                employee: "",
                start_date: "",
                end_date: "",
                child_ids: []
            });
        }

        setEmployees([]);
    };

    const startDate = watch("start_date");
    return (
        <div className="p-6 bg-gray-50 min-h-screen">

            {/* HEADER */}
            <div className="flex justify-between items-center bg-gradient-to-r from-brand-500 to-brand-700 text-white rounded-xl p-6 shadow-lg">
                <h1 className="text-3xl font-bold">Delivery Assignment</h1>

                <div className="flex flex-row gap-5">
                    <button
                        onClick={fetchUnassignedChildren}
                        className="flex items-center gap-2 bg-white text-brand-500 px-4 py-2 rounded-lg font-semibold shadow"
                    >
                        <Eye /> UnAssigned Children
                    </button>
                    {canCreate && (
                        <button
                            onClick={async () => {
                                setEmployees([]);

                                if (isBranchIncharge) {
                                    reset({
                                        branch: branchCode,
                                        parent: "",
                                        employee: "",
                                        start_date: "",
                                        end_date: "",
                                        child_ids: []
                                    });

                                    await handleBranchChange(branchCode); // load dropdown data first
                                } else {
                                    reset({
                                        branch: "",
                                        parent: "",
                                        employee: "",
                                        start_date: "",
                                        end_date: "",
                                        child_ids: []
                                    });

                                    if (isZoneManager) {
                                        await fetchEmployeesByZone();
                                    }
                                }

                                setOpenModal(true); // open modal after data loaded
                            }}
                            className="flex items-center gap-2 bg-white text-brand-500 px-4 py-2 rounded-lg font-semibold shadow"
                        >
                            <Plus /> Assign
                        </button>
                    )}
                </div>

            </div>

            {/* TABLE */}
            <GenericTable
                title="Assigned Deliveries"
                columns={columns}
                data={assignments}
                loading={loading}
            />

            {/* MODAL */}
            {openModal && (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-[99999] p-4">
        <div className="bg-white p-6 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative">

                        <button
                            onClick={handleCloseModal}
                            className="absolute top-3 right-3"
                        >
                            <X />
                        </button>

                        <h2 className="text-xl font-bold mb-4">Assign Delivery</h2>

                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

                            {showBranchDropdown && (
                                <Dropdown
                                    name="branch"
                                    label="Select Branch"
                                    control={control}
                                    options={branches}
                                    error={errors.branch?.message}
                                    required
                                    onChange={(val) => handleBranchChange(val)}
                                />)}

                            <Dropdown
                                name="parent"
                                label="Select Parent"
                                control={control}
                                options={parentOptions}
                                error={errors.parent?.message}
                                required
                            />

                            <Dropdown
                                name="child_ids"
                                label="Select Children"
                                control={control}
                                options={childrenOptions}
                                error={errors.child_ids?.message}
                                required
                                isMulti
                                disabled={!selectedParent}
                            />

                            <Dropdown
                                name="employee"
                                label="Select Delivery Person"
                                control={control}
                                options={employeeOptions}
                                error={errors.employee?.message}
                                required
                            />
                            <DatePicker
                                control={control}
                                name="start_date"
                                label="Start Date"
                                required
                                error={errors.start_date?.message}
                                minDate={new Date().toISOString().split("T")[0]}
                            />

                            <DatePicker
                                control={control}
                                name="end_date"
                                label="End Date"
                                disabled={!startDate}
                                error={errors.end_date?.message}
                                minDate={startDate || new Date().toISOString().split("T")[0]}
                            />

                            <button
                                type="submit"
                                className="w-full bg-brand-500 text-white py-3 rounded-lg"
                            >
                                {submitLoading ? "Assigning..." : "Assign"}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {openUnassignedModal && (
                <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
                    <div className="bg-white w-full max-w-2xl rounded-xl p-6 max-h-[80vh] overflow-y-auto relative">

                        <button
                            onClick={() => setOpenUnassignedModal(false)}
                            className="absolute top-3 right-3"
                        >
                            <X />
                        </button>

                        <h2 className="text-xl font-bold mb-4">
                            UnAssigned Children
                        </h2>

                        {unassignedLoading ? (
                            <p>Loading...</p>
                        ) : unassignedChildren.length === 0 ? (
                            <p>No unassigned children found</p>
                        ) : (
                            <div className="space-y-3">
                                {unassignedChildren.map((parent, idx) => (
                                    <div
                                        key={idx}
                                        className="border rounded-lg overflow-hidden"
                                    >
                                        {/* Header */}
                                        <button
                                            className="w-full px-4 py-3 bg-gray-100 flex justify-between items-center font-semibold"
                                            onClick={() =>
                                                setExpandedParent(
                                                    expandedParent === idx ? null : idx
                                                )
                                            }
                                        >
                                            <span>{parent.Username}</span>
                                            <span>
                                                {expandedParent === idx ? "-" : "+"}
                                            </span>
                                        </button>

                                        {/* Body */}
                                        {expandedParent === idx && (
                                            <div className="p-4 space-y-2 bg-white">
                                                {parent.Child.map((child: any) => (
                                                    <div
                                                        key={child.Child_Id}
                                                        className="border rounded-lg px-3 py-2"
                                                    >
                                                        <p className="font-medium">
                                                            {child.Child_Name}
                                                        </p>
                                                        <p className="text-sm text-gray-500">
                                                            {child.Child_Id}
                                                        </p>
                                                        <p className="text-xs text-blue-600">
                                                            {child.Branch_Code}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}